import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import { propertyKeys } from '../queries';

/**
 * Archivieren, Pausieren und Fortsetzen eines Apartments samt Vorprüfung.
 *
 * Steht getrennt vom Layout, damit dort nur noch Struktur liegt. Das Verhalten ist unverändert.
 */
export function useApartmentActions(id: string, archiveOpen: boolean, onArchived: () => void) {
  const api = useApi();
  const qc = useQueryClient();

  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: propertyKeys.detail(id) }),
      qc.invalidateQueries({ queryKey: propertyKeys.lists() }),
    ]);

  const preflight = useQuery({
    queryKey: [...propertyKeys.detail(id), 'archive-preflight'],
    queryFn: () => api.properties.archivePreflight(id),
    enabled: archiveOpen,
    retry: false,
  });

  const archive = useMutation({
    mutationFn: (reason?: string) => api.properties.archive(id, reason),
    onSuccess: async (result) => {
      if (!result.archived) {
        await preflight.refetch();
        return;
      }
      onArchived();
      await invalidate();
      toast.success('Apartment archiviert');
    },
    onError: async (err) => {
      const apiError = await toApiError(err);
      toast.error(apiError.detail ?? apiError.title);
      await preflight.refetch();
    },
  });

  const lifecycleMutation = useMutation({
    mutationFn: (action: 'pause' | 'resume') =>
      action === 'pause' ? api.properties.pause(id) : api.properties.activate(id),
    onSuccess: async (_result, action) => {
      await invalidate();
      toast.success(
        action === 'pause'
          ? 'Apartment pausiert — der Kanalmanager schließt es, und es nimmt keine Buchungen mehr an.'
          : 'Apartment fortgesetzt — freie Nächte gehen wieder auf.',
      );
    },
    onError: async (err) => {
      const apiError = await toApiError(err);
      toast.error(apiError.detail ?? apiError.title);
    },
  });

  return { preflight, archive, lifecycleMutation };
}
